import React, { useState, useEffect } from "react";
import CardImg from "../../../components/CardImg";
import Collapse from "../../../components/Collapse";
import { formatBook as bookText, formatOdds, formatAmount } from "../../../utils/formatOdds";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import RefResultModal from "../../casinoComponents/refResultModal";
import Teen1DayResultBody from "../../casinoComponents/refResultModal/Teen1DayResultBody";
import { getCardImage } from "../../casinoComponents/resultVisuals/cardAssets";
import { getLastResults, getDetailResults } from "../../../apiservices/CasionApi";
import { formatRefMatchTime } from "../../../utils/refMatchTime";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import { latestBets } from "../../../utils/casinoMyBets";
import { VTEEN_RULES_HTML } from "./vteenRules";
import "../../../styles/games/teenunique.css";

/**
 * Virtual Teenpatti 1-day (vcasino/teen, API type vteen) in the reference markup:
 *   .casino-table.vcasino.vteen1day >
 *     [.progress.mobile-timer (ft > 3)]
 *     .casino-video > .casino-video-title + .video-box-container + .vcasino-cards + right icons (home, rules)
 *     .casino-detail > .main-odds (.odds-title + Main + Consecutive back/lay rows)
 *                    + .others-odds (ODD / EVEN rows, one box per card) + [remark] + .casino-video-last-results
 *   right sidebar: My Bets card only (UserName / Rate / Amount).
 * Result modal body = Teenpatti 1-day (same template as pteen in the reference).
 */

const WINNER_LEFT = `${import.meta.env.BASE_URL}img/winner-left.png`;
const REMARK_ICON = `${import.meta.env.BASE_URL}assets/img/icons/remark.png`;
const EMPTY = { b: 0, l: 0, min: 0, max: 0, gstatus: "SUSPENDED" };

// Reference v-kvalmin / v-kvalmax: 25 → "25", 25000 → "25K", 100000 → "1L"
function kval(num) {
  const n = Number(num) || 0;
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(1))}K`;
  return String(n);
}

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

function formatBetDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Reference vcasino RightSideBar for vteen: the My Bets card only (no rules card)
function VRightSide({ bets = [], onViewMore }) {
  const list = latestBets(bets); // latest 10, like the reference
  return (
    <>
      <div className="card m-b-10 my-bet" id={IS_REF_MOBILE ? "casino-bets" : undefined}>
        <div className="card-header">
          <h6 className="card-title float-left">My Bets</h6>{" "}
          <a
            href="javascript:void(0)"
            className="btn btn-back float-right"
            onClick={(e) => {
              e.preventDefault();
              if (typeof onViewMore === "function") onViewMore();
            }}
          >
            View More
          </a>
        </div>{" "}
        <div className="card-body1">
          <div className="tab-content">
            <div id="matched-bet" className="tab-pane active">
              <div className="table-responsive1">
                <table className="table coupon-table table-striped mb-0">
                  <thead>
                    <tr>
                      <th style={{ minWidth: "90px" }}>UserName</th>
                      <th className="text-right" style={{ minWidth: "50px" }}>Rate</th>
                      <th className="text-right" style={{ minWidth: "70px" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">No records found</td>
                      </tr>
                    ) : (
                      list.map((bet, i) => {
                        const t = String(bet.type || bet.gametype || "").toLowerCase();
                        const border = t === "lay" ? "lay-border" : t === "back" ? "back-border" : undefined;
                        return (
                          <React.Fragment key={bet.id || i}>
                            <tr className={border}>
                              <td colSpan={4}>
                                <b>{bet.selection || bet.matchedBet || "-"}</b>{" "}
                                <span className="float-right">{formatBetDate(bet.placeDate)}</span>
                              </td>
                            </tr>
                            <tr className={border}>
                              <td>{bet.userName}</td>
                              <td className="text-right">{formatOdds(bet.odds)}</td>
                              <td className="text-right">{formatAmount(bet.stake)}</td>
                            </tr>
                            <tr>
                              <td colSpan={4} style={{ height: "3px", padding: 0 }} />
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Last Result items (LastResultsSection for vteen): span.resulta "A" / span.resultb "B" / "T"
function VTeenResults({ gameType, fallbackTitle }) {
  const apiType = String(gameType || "vteen").toLowerCase().trim();
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(apiType);
        const res = response?.data?.data?.res;
        if (Array.isArray(res)) setLastResults(res);
      } catch (e) {
        console.error("Error fetching last results:", e);
      }
    };
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [apiType]);

  const handleResultClick = async (mid) => {
    if (loading || !mid) return;
    setLoading(true);
    try {
      const response = await getDetailResults(apiType, String(mid));
      const t1 = response?.data?.data?.t1 ?? response?.data?.t1;
      if (t1) {
        setDetailResult(t1);
        setShowModal(true);
      }
    } catch (e) {
      console.error("Error fetching detail results:", e);
    }
    setLoading(false);
  };

  return (
    <>
      {lastResults.map((r, i) => {
        const w = String(r?.win ?? "");
        return (
          <span
            key={`${r?.mid}-${i}`}
            className={w === "1" ? "resulta" : w === "2" ? "resultb" : undefined}
            onClick={() => handleResultClick(r?.mid)}
          >
            {w === "1" ? "A" : null} {w === "2" ? "B" : null} {w === "0" ? "T" : null}
          </span>
        );
      })}
      {loading && createPortal(<div className="loader-overlay" />, document.querySelector(".page-content") || document.body)}
      <RefResultModal
        show={showModal && !!detailResult}
        onHide={() => setShowModal(false)}
        title={`${detailResult?.ename || fallbackTitle} Result`}
        roundId={detailResult?.rid}
        matchTime={formatRefMatchTime(detailResult?.mtime)}
      >
        {detailResult && <Teen1DayResultBody t1={detailResult} />}
      </RefResultModal>
    </>
  );
}

export default function VTeen1DayView({
  gameName = "vteen",
  title = "V-Teenpatti 1-day",
  data,
  exposures = {},
  onBetClick,
  myBets,
  onViewMore,
  viewMoreModal,
}) {
  // Bootstrap collapse in the reference: each range popup toggles independently
  const [openRanges, setOpenRanges] = useState(() => new Set());
  const isOpen = (id) => openRanges.has(id);
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const sub = Array.isArray(data?.sub) ? data.sub : [];
  const ofType = (t) => sub.filter((s) => s?.subtype === t).sort(bySr);
  const main = ofType("vteen");
  const consecutive = ofType("vcon");
  const oddEven = ofType("voddeven");
  const at = (arr, i) => arr[i] || EMPTY;
  const cards = data?.card ? String(data.card).split(",").map((c) => c.trim()) : [];
  const ft = Number(data?.ft) || 0;
  const lt = Number(data?.lt) || 0;
  const rdesc = String(data?.rdesc ?? "");
  const remark = data?.remark;

  const exposureOf = (...keys) => {
    for (const k of keys) {
      const v = exposures?.[k];
      if (v != null && v !== 0) return Number(v) || 0;
    }
    return 0;
  };
  const bookClass = (v) => (v > 0 ? "book-green" : "book-red");

  const suspended = (item) => String(item?.gstatus || "").toUpperCase() !== "OPEN";
  const click = (item, odd, label, type) => {
    if (suspended(item)) return;
    onBetClick?.(odd, label, type, item);
  };

  const toggleRange = (id) =>
    setOpenRanges((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const rangeIcon = (id, extra = "") => (
    <i
      role="button"
      tabIndex={0}
      data-toggle="collapse"
      data-target={`#${id}`}
      aria-expanded={extra || isOpen(id) ? (isOpen(id) ? "true" : "false") : undefined}
      className={`fas fa-info-circle${extra && !isOpen(id) ? " collapsed" : ""}`}
      onClick={() => toggleRange(id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleRange(id);
        }
      }}
    />
  );

  // Main / Consecutive rows: back + lay boxes and the book under them
  const blBox = (item, exposure, label) => (
    <div className="odds-box">
      <div className="casino-bl-box">
        <div
          className={`back casino-bl-box-item${suspended(item) ? " suspended" : ""}`}
          onClick={() => click(item, item.b, label, "back")}
        >
          <span className="casino-box-odd">{item.b}</span>
        </div>{" "}
        <div
          className={`lay casino-bl-box-item${suspended(item) ? " suspended" : ""}`}
          onClick={() => click(item, item.l, label, "lay")}
        >
          <span className="casino-box-odd">{item.l}</span>
        </div>
      </div>{" "}
      <div className={`w-100 odds-book text-center ${bookClass(exposure)}`}>{bookText(exposure)}</div>
    </div>
  );

  const mainSection = (label, rangeNo, items, keyPrefix) => {
    const id = `range${rangeNo}`;
    const a = at(items, 0);
    const b = at(items, 1);
    return (
      <div className="odds-section">
        {blBox(a, exposureOf(keyPrefix + a.nat, a.nat), a.nat)}{" "}
        <div className="odds-box pt-2">
          <div className="vminmax">
            <b>{label}</b>{" "}
            <div className="d-inline-block ml-2">
              {rangeIcon(id, "collapsed")}{" "}
              <Collapse id={id} open={!!isOpen(id)} className="icon-range">
                {"\n                                                R:"}
                <span>{kval(b.min)}</span>-<span>{kval(b.max)}</span>
              </Collapse>
            </div>
          </div>
        </div>{" "}
        {blBox(b, exposureOf(keyPrefix + b.nat, b.nat), b.nat)}
      </div>
    );
  };

  // ODD / EVEN rows: one back box + book per card (voddeven items)
  const oddEvenSection = (which, rangeNo) => {
    const id = `range${rangeNo}`;
    const first = at(oddEven, 0);
    const label = which.toUpperCase();
    return (
      <div className="odds-section">
        <div className="odds-box w-100">
          <div className="d-inline-block pr">
            <b>{label}</b>{" "}
            <div className="d-inline-block ml-2">
              {rangeIcon(id)}{" "}
              <Collapse id={id} open={!!isOpen(id)} className="icon-range">
                {"\n                                                R:"}
                <span>{kval(first.min)}</span>-<span>{kval(first.max)}</span>
              </Collapse>
            </div>
          </div>
        </div>{" "}
        {oddEven.map((item, i) => {
          const odds = Array.isArray(item?.odds) ? [...item.odds].sort((x, y) => (x?.sno || 0) - (y?.sno || 0)) : [];
          return (
            <div className="odds-box" key={item?.sid ?? i}>
              <div className="casino-bl-box">
                {odds.map((o, j) =>
                  String(o?.nat || "").toLowerCase() === which ? (
                    <React.Fragment key={j}>
                      <div
                        className={`back casino-bl-box-item${suspended(item) ? " suspended" : ""}`}
                        onClick={() => click(item, o.b, `${item.nat} ${o.nat}`, "back")}
                      >
                        <span className="casino-box-odd">{o.b}</span>
                      </div>{" "}
                      {(() => {
                        const v = exposureOf(`${item.nat} ${o.nat}`, `${item.nat}-${o.nat}`);
                        return <div className={`casino-book ${bookClass(v)}`}>{bookText(v)}</div>;
                      })()}
                    </React.Fragment>
                  ) : null
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const card = (token) => (
    <span>
      <CardImg src={getCardImage(token)} />
    </span>
  );

  return (
    <CasinoTableLayout tableClass="vcasino vteen1day" rightSide={<VRightSide bets={myBets} onViewMore={onViewMore} />}>
      <>
        {ft > 3 && (
          <div className="progress mobile-timer">
            <div
              className={`progress-bar ${lt <= 3 ? "bg-danger" : lt <= 6 ? "bg-warning" : "bg-success"}`}
              style={{ width: `${((100 * lt) / ft).toFixed(2)}%` }}
            />
          </div>
        )}{" "}
        <div className="casino-video">
          <div className="casino-video-title">
            <span className="casino-name">{title}</span>{" "}
            <div className="casino-video-rid">Round ID: {data?.mid}</div>
          </div>{" "}
          <div className="video-box-container">
            <div className="video-box" />
          </div>{" "}
          {cards.length > 0 && (
            <div className="vcasino-cards">
              <div className="player-card playera-card">
                {rdesc === "1" && <img src={WINNER_LEFT} className="v-winner" />}{" "}
                <div className="w-100 playera mb-2">Player A</div> {card(cards[0])} {card(cards[2])} {card(cards[4])}
              </div>{" "}
              <div className="player-card playerb-card">
                {rdesc === "2" && <img src={WINNER_LEFT} className="v-winner" />}{" "}
                <div className="w-100 playerb mb-2">Player B</div> {card(cards[5])} {card(cards[3])} {card(cards[1])}
              </div>
            </div>
          )}{" "}
          <div className="casino-video-right-icons">
            <div title="Home" className="casino-video-home-icon">
              <Link to="/admin/vcasino/list" style={{ color: "var(--text-highlight)" }}>
                <i className="fas fa-home" />
              </Link>
            </div>{" "}
            <div
              title="Rules"
              className="casino-video-rules-icon"
              onClick={() => {
                setShowRules(true);
                if (IS_REF_MOBILE) setShowRulesModal(true);
              }}
            >
              <i className="fas fa-info-circle" />
            </div>
          </div>{" "}
          <div>
            {showRules && (
              <div
                id="casino-vieo-rules"
                aria-modal="true"
                className="casino-vieo-rules show d-none-small show-rules"
                style={{ display: "block" }}
              >
                <div className="rules-header">
                  <div>Rules</div> <i className="fas fa-times" onClick={() => setShowRules(false)} />
                </div>{" "}
                <div className="rules-body" dangerouslySetInnerHTML={{ __html: VTEEN_RULES_HTML }} />
              </div>
            )}
            <Modal
              show={showRulesModal}
              onHide={() => setShowRulesModal(false)}
              className="casino-rules teenpatti2-rule-modal"
              dialogClassName="modal-md"
              id="rules-modal"
            >
              <header className="modal-header">
                <h5 className="modal-title">Rules</h5>{" "}
                <button type="button" aria-label="Close" className="close" onClick={() => setShowRulesModal(false)}>
                  ×
                </button>
              </header>
              <Modal.Body>
                <div className="casino-vieo-rules show-rules show">
                  <div className="rules-body" dangerouslySetInnerHTML={{ __html: VTEEN_RULES_HTML }} />
                </div>
              </Modal.Body>
            </Modal>
          </div>
        </div>{" "}
        {data?.mid ? (
          <div className="casino-detail">
            <div className="main-odds">
              <div className="odds-title">
                <div className="playera">Player A</div> <div>{" "}</div> <div className="playerb">Player B</div>
              </div>{" "}
              {mainSection("Main", 7, main, "")}{" "}
              {mainSection("Consecutive", 12, consecutive, "Consecutive ")}
            </div>{" "}
            <div className="others-odds">
              {oddEvenSection("odd", 13)}{" "}
              {oddEvenSection("even", 14)}
            </div>{" "}
            {remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={REMARK_ICON} />
                </div>{" "}
                <marquee>{remark}</marquee>
              </div>
            ) : null}{" "}
            <div className="casino-video-last-results">
              <VTeenResults gameType={gameName} fallbackTitle={title} />
            </div>
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
